/**
 * Shared check-display strings — THE single canonical source for every
 * user-facing check status label and shared body string rendered by BOTH
 * mirrors (PDF templates and dashboard components). Amendment §A2 /
 * Permanent Rule 12: both surfaces render identical text from the same
 * builder; Amendment §A3 (as amended v3.17/v3.18): the status vocabulary
 * and the report-only / no-table display contracts.
 *
 * STAGE4-FIX-B: extends (and renames) shared/reportOnlyDisplay.js into the
 * single check-display source. Neither surface may re-type any string in
 * this module.
 *
 * Isomorphic — string/number only, safe to import from browser-bundled
 * dashboard components.
 *
 * @module shared/checkDisplay
 */

/**
 * The complete user-facing status vocabulary (§A3 as amended). Every status
 * chip on either surface renders one of these exact strings — the PDF's CSS
 * uppercase transform is a no-op on them, so the PDF text layer and the
 * dashboard DOM carry identical bytes.
 */
export const STATUS_LABEL = Object.freeze({
  PASS: 'PASS',
  EXCEEDED: 'EXCEEDED',
  STRUCTURAL_FINDING: 'STRUCTURAL FINDING',
  REPORT_ONLY: 'REPORT ONLY',
  COMPARISON: 'COMPARISON',
});

/**
 * THE status-label mapping (§A3). Inputs are the canonical CheckResult
 * fields; output is the exact user-facing label. The PDF assembler
 * (buildV6ResultDisplay) and the dashboard chip both consume this — no
 * local label mapping may exist on either surface.
 *
 *   report_only                → REPORT ONLY   (incl. the comparison checks
 *                                               #31–35, whose per-check chip
 *                                               is the neutral REPORT ONLY
 *                                               per the BASELINE-SUB4 ruling;
 *                                               COMPARISON is the CATEGORY-
 *                                               level marker)
 *   schedule_level + EXCEEDED  → STRUCTURAL FINDING
 *   EXCEEDED                   → EXCEEDED
 *   otherwise                  → PASS
 *
 * @param {string} statusThreshold — check.status_threshold (§A1)
 * @param {string} findingType    — check.finding_type (§A1)
 * @returns {string} one of STATUS_LABEL
 */
export function statusLabelFor(statusThreshold, findingType) {
  if (findingType === 'report_only') return STATUS_LABEL.REPORT_ONLY;
  // F1 (D-Check37ReportOnlyChip, v3.36): a check FORCED to REPORT_ONLY status
  // while keeping a non-report finding_type — the unused-calendar-only #37,
  // which stays schedule_level but carries status_threshold REPORT_ONLY
  // (dataIntegrity.js) — must chip REPORT ONLY, not fall through to PASS. The
  // cover informational count (verdict.js) and Top Issues label
  // (deriveTopIssueSeverityLabel) already treat this state as informational;
  // this closes the per-check chip to agree with them.
  if (statusThreshold === 'REPORT_ONLY') return STATUS_LABEL.REPORT_ONLY;
  if (statusThreshold === 'EXCEEDED') {
    return findingType === 'schedule_level'
      ? STATUS_LABEL.STRUCTURAL_FINDING
      : STATUS_LABEL.EXCEEDED;
  }
  return STATUS_LABEL.PASS;
}

/**
 * The chip text on the report-only body (both surfaces). Same bytes as the
 * §A3 label so the PDF text layer and the dashboard DOM agree exactly.
 */
export const REPORT_ONLY_CHIP_LABEL = STATUS_LABEL.REPORT_ONLY;

/**
 * The neutral body line for summary-only ("noTable") checks — #9/#20/#21/#22:
 * their finding is carried entirely by the result/threshold line above, so
 * the body is this single neutral line on BOTH surfaces (never the
 * pass-styled empty state).
 */
export const NO_TABLE_BODY_TEXT = 'See check summary above.';

/**
 * The count line beside the report-only chip: "N item(s) reported ·
 * informational, no score impact". Zero is a legitimate value
 * ("0 items reported").
 *
 * @param {number} count — the TRUE finding total (severity_findings_total
 *   where the projection caps the array).
 * @returns {string}
 */
export function buildReportOnlyLine(count) {
  const n = Number.isFinite(count) ? count : 0;
  return `${n} ${n === 1 ? 'item' : 'items'} reported · informational, no score impact`;
}
